import React from 'react';

import { Box, Flex, Skeleton, Stack } from '@chakra-ui/react';
import { useGoalsWeight } from 'components/GoalsWeight/GoalsWeightContext';
import { renderParentIcon } from 'components/GoalsWeight/GoalsWeightSidepanelV1';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCurrentGoalQuestionIndex,
  setCurrentMLInputSelectedQuestionId,
  useCurrentGoalQuestionIndex,
  useCurrentMLInputSelectedQuestionId,
  useIsValidateForm,
} from 'reducers/reviewFormSlice';
import MLCommentTextGenerationCompV1 from 'screens/common/MLCommentTextGenerationCompV1/MLCommentTextGenerationCompV1';
import { checkRequiredValidationOK } from 'screens/performanceReview/helpers';
import { useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useIsManagerView from 'screens/performanceReview/hooks/useIsManagerView';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import {
  ChangeWeightage,
  Help,
  KeyResultIcon,
  KeyResultPrivate2,
  ObjectiveIcon,
  PrivateObjective,
  Warning,
} from 'assets/assetsV1/iconComponents';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { isMobileView } from 'constants/isMobileView';
import { sendClickedManageGoalWeightageEvent } from 'constants/mixpanelHelpers/reviewsEvents';

import { updateQuestionResponse } from 'services/performanceReview';

import { AccordionButtonV2, AccordionItemV2, AccordionPanelV2, AccordionV2 } from 'uiToolkitV1/AccordionV2';
import { Button } from 'uiToolkitV1/Button';
import { Paper } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { QuestionRenderer, ViewOnlyQuestionRenderer } from '../QuestionRenderer/QuestionRenderer';
import QuestionsNavigationFooter from '../QuestionsNavigationFooter/QuestionsNavigationFooter';
import ScrollToTopComp from '../ScrollToTopComp';
import { useReviewFormSidebarDataOld } from '../Sidebar/hooks/dataHooks';
import { QuestionResponseStatusTag } from '../newFormComponents/QuestionResponseStatusTag';
import GoalScrollableNav from './components/GoalScrollableNav';
import { useGoalFormData, useGoalFormMutation, useInvalidateGoalFormData } from './hooks/dataHooks';

const WeightageChangeComp = ({
  providerType,
  employeeId,
  isShowGoalWeightage,
  okrTotalWeight,
  refreshFormDataHandler,
  feedbackFormId,
  reviewId,
}) => {
  const { showSidePanel, hideSidePanel } = useGoalsWeight();
  const isWeightage100Percent = okrTotalWeight === 100;
  const { moduleName } = useOrganisationSettings();

  const openMyWeightageSidePanel = () => {
    sendClickedManageGoalWeightageEvent({
      eventProperties: {
        empId: employeeId,
        reviewCycleId: reviewId,
      },
    });
    showSidePanel({
      employeeId,
      canEdit: true,
      preSelectedOptionId: feedbackFormId,
      onCancel: () => {
        hideSidePanel();
      },
      onSuccess: () => {
        hideSidePanel();
        refreshFormDataHandler();
      },
    });
  };

  let text;
  if (!isShowGoalWeightage) {
    text = 'Please note that all the below mentioned goals carry equal weights';
  } else if (providerType === REVIEW_PROVIDER_TYPES.SELF) {
    text = isWeightage100Percent ? (
      `To view/change your current weightage distribution across ${modifyString(
        moduleName,
        StringModifier.SENTENCE_CASE
      )}`
    ) : (
      <Flex alignItems="center">
        <Warning boxSize="24px" color="icon.g1" mr={2} />
        Uh-oh! Your current goal weightage distribution sums up to
        <TextBodyMedium color="functional.C62C02" bold display="inline" ml={1}>
          {okrTotalWeight}%
        </TextBodyMedium>
        . Do you want to re-distribute?
      </Flex>
    );
  } else {
    text = isWeightage100Percent ? (
      `To view/change the weightage distribution of your reportee across ${modifyString(
        moduleName,
        StringModifier.SENTENCE_CASE
      )}`
    ) : (
      <Flex alignItems="center">
        <Warning boxSize="24px" color="icon.g1" mr={2} />
        Uh-oh! Your reportee&apos;s current goal weightage distribution sums up to
        <TextBodyMedium color="functional.C62C02" bold display="inline" ml={1}>
          {okrTotalWeight}%
        </TextBodyMedium>
        . Do you want to re-distribute?
      </Flex>
    );
  }

  if (![REVIEW_PROVIDER_TYPES.SELF, REVIEW_PROVIDER_TYPES.MANAGER].includes(providerType)) return null;

  return (
    <Flex alignItems="center" mt="-12px" mb={1}>
      <TextBodyMedium bold my={2}>
        {text}
      </TextBodyMedium>
      {isShowGoalWeightage && (
        <Button onClick={openMyWeightageSidePanel} variant="tertiary" StartIcon={ChangeWeightage} ml={4}>
          Manage {moduleName} weightage
        </Button>
      )}
    </Flex>
  );
};

const GoalForm = () => {
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const isValidateForm = useIsValidateForm();
  const { isManagerView } = useIsManagerView();

  const { isPreview } = useReviewPreviewParams();

  const { data: formData = {} } = useReviewFormSidebarDataOld({
    employeeId,
    providerId,
    reviewId,
  });

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const providerType = formData?.review?.providerType;
  const feedbackFormId = formData?.reviewInfo?.feedbackForm?.id;
  const isShowGoalWeightage = formData?.isShowGoalWeightage;
  const revieweeEmployee = formData?.employee;

  const dispatch = useDispatch();
  const currentQuestionIndex = useCurrentGoalQuestionIndex();
  const currentMLInputSelectedQuestionId = useCurrentMLInputSelectedQuestionId();

  const { data, isLoading } = useGoalFormData({
    employeeId,
    providerId,
    // responseId, // <- will be used to show the submitted
    reviewId,
  });
  const goalFormInvalidateHandler = useInvalidateGoalFormData({
    employeeId,
    providerId,
    // responseId, // <- will be used to show the submitted
    reviewId,
  });

  const formMutation = useGoalFormMutation({ answerQuestion: updateQuestionResponse });

  const { mergedGoalQuestions, okrTotalWeight } = data ?? {};

  const setCurrentIndex = ({ index }) => {
    dispatch(setCurrentGoalQuestionIndex(index !== currentQuestionIndex ? index : -1));
  };

  const answerQuestionCurried =
    ({
      // eslint-disable-next-line no-shadow
      reviewId,
      // eslint-disable-next-line no-shadow
      employeeId,
      goalId,
      questionId,
      // existing answer values
      // as BE doesn't support partial updates
      // we've to pass existing values when answering
      currentFeedBackText,
      currentFeedBackTextHtml,
      currentSelectedAnswerOptions,
      goalQuestionsIndex,
      questionIndex,
    }) =>
    async ({
      feedbackText = currentFeedBackText,
      feedbackTextHtml = currentFeedBackTextHtml,
      selectedAnswerOptions = currentSelectedAnswerOptions,
    }) => {
      formMutation.mutate({
        reviewId,
        goalId,
        employeeId,
        questionId,
        feedbackText,
        feedbackTextHtml,
        selectedAnswerOptions,
        sourceForm: 'Goal form',
        goalQuestionsIndex,
        questionIndex,
        isPreview,
      });
    };

  if (isLoading) {
    return (
      <Stack>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  }

  const isGoalAssociatedQuestions = mergedGoalQuestions?.[0]?.goal !== null || mergedGoalQuestions?.[0]?.okr !== null;

  return (
    <>
      {isGoalAssociatedQuestions && !isMobileView() && (
        <WeightageChangeComp
          providerType={providerType}
          employeeId={employeeId}
          isShowGoalWeightage={isShowGoalWeightage}
          okrTotalWeight={okrTotalWeight}
          refreshFormDataHandler={goalFormInvalidateHandler}
          feedbackFormId={feedbackFormId}
          reviewId={reviewId}
        />
      )}
      <Paper
        p="0"
        w="100%"
        flexGrow={1}
        style={{
          boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.24)',
        }}
      >
        <AccordionV2 openPanelIndex={currentQuestionIndex}>
          {mergedGoalQuestions?.map(({ questions, goal, okr }, mergedQuestionsIndex) => {
            const currentMLSelectedQuestionIndex = questions.findIndex(
              (question) => question.id === currentMLInputSelectedQuestionId
            );
            const currentMLSelectedQuestion = questions?.[currentMLSelectedQuestionIndex] ?? null;
            if (
              currentMLSelectedQuestion &&
              currentMLSelectedQuestion?.isViewOnlyModeEnabled !== true &&
              currentMLSelectedQuestion.enableFeedbackText &&
              currentQuestionIndex === mergedQuestionsIndex
            ) {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <AccordionItemV2 key={mergedQuestionsIndex}>
                  <AccordionPanelV2>
                    <Paper p="0" w="100%" flexGrow={1}>
                      <MLCommentTextGenerationCompV1
                        reviewId={reviewId}
                        questionId={currentMLSelectedQuestion.id}
                        revieweeId={revieweeEmployee?.id}
                        revieweeDisplayName={revieweeEmployee?.displayName}
                        reviewerId={loggedInEmployee.id}
                        reviewerDisplayName={loggedInEmployee.displayName}
                        onChange={answerQuestionCurried({
                          goalId: goal?.id,
                          employeeId,
                          reviewId,
                          questionId: currentMLSelectedQuestion?.id,
                          currentFeedBackText: currentMLSelectedQuestion?.feedbackText,
                          currentFeedBackTextHtml: currentMLSelectedQuestion?.feedbackTextHtml,
                          currentSelectedAnswerOptions: currentMLSelectedQuestion?.selectedAnswerOptions,
                          goalQuestionsIndex: mergedQuestionsIndex,
                          questionIndex: currentMLSelectedQuestionIndex,
                        })}
                        onClose={() => dispatch(setCurrentMLInputSelectedQuestionId(null))}
                        textHtml={currentMLSelectedQuestion.feedbackTextHtml}
                        plainText={currentMLSelectedQuestion.feedbackText}
                      />
                    </Paper>
                  </AccordionPanelV2>
                </AccordionItemV2>
              );
            }

            return (
              <AccordionItemV2
                isError={!checkRequiredValidationOK({ questions, isValidateForm })}
                // eslint-disable-next-line react/no-array-index-key
                key={mergedQuestionsIndex}
              >
                <AccordionButtonV2
                  onClick={() =>
                    setCurrentIndex({
                      index: mergedQuestionsIndex,
                    })
                  }
                >
                  <Flex justifyContent="space-between" alignItems="center" flexGrow={1}>
                    <Flex flexGrow={1} mr={4} flexDirection="column">
                      {/* if goal is present then we will show parentOKRBasicDetails else show okr */}

                      {goal && (
                        <Flex alignItems="center">
                          {renderParentIcon(okr?.parentGoalBasicDetails?.goalType)}
                          <TextLabel color="text.g2" textAlign="left">
                            {okr?.parentGoalBasicDetails?.goalTitle ?? 'No parent'}
                          </TextLabel>
                        </Flex>
                      )}

                      <Flex alignItems="center">
                        {okr === null && <Help color="icon.g2" boxSize="24px" mr={2} />}
                        {okr?.type === 'OBJECTIVE' &&
                          (okr?.visibilityType === 'PRIVATE' ? (
                            <PrivateObjective color="functional.7651BC" boxSize="24px" mr={2} />
                          ) : (
                            <ObjectiveIcon color="functional.7651BC" boxSize="24px" mr={2} />
                          ))}
                        {okr?.type === 'GOAL' &&
                          (okr?.visibilityType === 'PRIVATE'
                            ? okr !== null && <KeyResultPrivate2 color="functional.7651BC" boxSize="24px" mr={2} />
                            : okr !== null && <KeyResultIcon color="functional.7651BC" boxSize="24px" mr={2} />)}
                        <TextBodyMedium bold textAlign="left">
                          {/*  in case of question without okr there would be only one question in the array - questions */}
                          {okr ? okr.title : `${questions?.[0]?.description} ${questions?.[0]?.isRequired ? ' *' : ''}`}
                        </TextBodyMedium>
                      </Flex>
                    </Flex>
                    <Flex>
                      <QuestionResponseStatusTag
                        questions={questions}
                        isValidateForm={isValidateForm}
                        isFormFillingPage
                      />
                    </Flex>
                  </Flex>
                </AccordionButtonV2>

                <AccordionPanelV2>
                  <Box position="relative" flexGrow={1} maxH="500px" w="100%">
                    <Flex p={isMobileView() ? '0px' : '16px 16px 16px 0'} w="100%" maxH="100%">
                      {(isManagerView || !!okr) && !isMobileView() && (
                        <Flex flexDir="column" maxW="50%" w="50%">
                          <GoalScrollableNav
                            questions={questions}
                            goalId={goal?.id}
                            okr={okr}
                            providerType={formData?.review?.providerType}
                            answerQuestionHandler={answerQuestionCurried({
                              goalId: goal?.id,
                              employeeId,
                              reviewId,
                            })}
                            isManagerAISummaryEnabled={currentQuestionIndex === mergedQuestionsIndex}
                            reviewId={reviewId}
                            revieweeId={revieweeEmployee?.id}
                          />
                        </Flex>
                      )}
                      <Flex
                        flexDir="column"
                        maxW={isMobileView() ? '100%' : '50%'}
                        p={4}
                        w={isMobileView() ? '100%' : '50%'}
                      >
                        <Flex flexDir="column" overflowY="auto" flexGrow={1}>
                          {questions?.map((goalQuestion, goalQuestionIndex) => (
                            <>
                              {goalQuestion?.isViewOnlyModeEnabled === true && (
                                <ViewOnlyQuestionRenderer
                                  {...goalQuestion}
                                  showQuestionText={!!okr}
                                  questionIndex={goalQuestionIndex}
                                  showHeading={
                                    goalQuestionIndex === 0 || !questions[goalQuestionIndex - 1].isViewOnlyModeEnabled
                                  }
                                />
                              )}
                              {goalQuestion?.isViewOnlyModeEnabled !== true && (
                                <QuestionRenderer
                                  answerQuestionHandler={answerQuestionCurried({
                                    goalId: goal?.id,
                                    employeeId,
                                    reviewId,
                                    questionId: goalQuestion?.id,
                                    currentFeedBackText: goalQuestion?.feedbackText,
                                    currentFeedBackTextHtml: goalQuestion?.feedbackTextHtml,
                                    currentSelectedAnswerOptions: goalQuestion?.selectedAnswerOptions,
                                    goalQuestionsIndex: mergedQuestionsIndex,
                                    questionIndex: goalQuestionIndex,
                                  })}
                                  showQuestionText={!!okr}
                                  {...goalQuestion}
                                  question={goalQuestion}
                                  isGoalSectionEditable={formData?.isGoalSectionEditable}
                                  questionIndex={goalQuestionIndex}
                                  showQuestionIndex={questions.length >= 1}
                                  goalId={goal?.id}
                                />
                              )}
                            </>
                          ))}
                        </Flex>
                        <QuestionsNavigationFooter questions={mergedGoalQuestions} />
                      </Flex>
                      <ScrollToTopComp
                        isVisible={currentQuestionIndex === mergedQuestionsIndex}
                        currentQuestionIndex={currentQuestionIndex}
                      />
                    </Flex>
                  </Box>
                </AccordionPanelV2>
              </AccordionItemV2>
            );
          })}
        </AccordionV2>
      </Paper>
    </>
  );
};

export default GoalForm;
